import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  UserCircle, Plus, Search, Layers, Edit3, Trash2, BookOpen, Key,
  Upload, Download, X, CheckCircle, AlertCircle, FileText
} from 'lucide-react';

const FacultyManagement = () => {
  const [facultyList, setFacultyList] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [userFormData, setUserFormData] = useState({
    user_name: '',
    user_type: 'faculty',  // Auto-filled
    password: '',
    email: '',  // Email for sending credentials
    status: 1,  // Default active
  });
  const [facultyFormData, setFacultyFormData] = useState({
    user_id: '',  // Will be auto-filled after user creation
    fac_name: '',
    designation: '',
    role: '',
    dept_id: '',
    join_date: '',
    email: '',
    phone: '',
  });
  const [showFacultyForm, setShowFacultyForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [editingFaculty, setEditingFaculty] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const fileInputRef = useRef(null);

  // Roles available for faculty (used in Edit Profile)
  // const roles = ['faculty', 'hod', 'coordinator', 'course_coordinator'];

  useEffect(() => {
    fetchDepartments();
    fetchFaculty();
  }, []);

  const fetchDepartments = async () => {
    try {
      const res = await axios.get('/api/departments');
      setDepartments(res.data);
    } catch (err) {
      console.error('Failed to load departments', err);
      setMessage({ text: 'Failed to load departments', type: 'error' });
    }
  };

  const fetchFaculty = async () => {
    try {
      const res = await axios.get('/api/faculty');
      setFacultyList(res.data);
    } catch (err) {
      console.error('Failed to load faculty', err);
      setMessage({ text: 'Failed to load faculty', type: 'error' });
    }
  };

  const validateUserForm = () => {
    const errors = {};
    
    if (!userFormData.user_name.trim()) {
      errors.user_name = 'Username is required';
    }
    
    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!userFormData.email.trim() || !emailRegex.test(userFormData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateFacultyForm = () => {
    const errors = {};
    
    // Only require: Faculty name and Department
    if (!facultyFormData.fac_name.trim()) errors.fac_name = 'Faculty name is required';
    if (!facultyFormData.dept_id) errors.dept_id = 'Department is required';
    
    // These are optional - faculty will fill them later
    // if (!facultyFormData.designation.trim()) errors.designation = 'Designation is required';
    // if (!facultyFormData.role) errors.role = 'Role is required';
    // if (!facultyFormData.join_date) errors.join_date = 'Join date is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUserInputChange = (e) => {
    const { name, value } = e.target;
    setUserFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleFacultyInputChange = (e) => {
    const { name, value } = e.target;
    setFacultyFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!validateUserForm()) return;

    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      // Use auto-generate password and send email
      const payload = {
        user_name: userFormData.user_name,
        user_type: 'faculty',
        auto_generate_password: true,
        email: userFormData.email,  // Send email for credentials
        status: 2  // First login (must change password)
      };
      
      const res = await axios.post('/api/users', payload);
      const newUserId = res.data.user_id;
       
      // Auto-fill user_id in faculty form
      setFacultyFormData((prev) => ({ ...prev, user_id: newUserId }));
      setShowFacultyForm(true);
      
      // Check if email was sent or if we got password as fallback
      if (res.data.email_error) {
        setMessage({ 
          text: `User created! Note: Email failed to send. Password: ${res.data.password}`, 
          type: 'warning' 
        });
      } else {
        setMessage({ 
          text: 'User account created! Credentials have been sent to email. Now add faculty details.', 
          type: 'success' 
        });
      }
      
      // Reset user form for next addition
      setUserFormData({
        user_name: '',
        user_type: 'faculty',
        password: '',
        email: '',
        status: 1,
      });
    } catch (err) {
      console.error(err);
      setMessage({
        text: err.response?.data?.error || 'Failed to create user account',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitFaculty = async (e) => {
    e.preventDefault();
    if (!validateFacultyForm()) return;

    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      if (editingFaculty) {
        // Edit existing faculty (no user changes)
        await axios.put(`/api/faculty/${editingFaculty.fac_id}`, facultyFormData);
        setMessage({ text: 'Faculty updated successfully!', type: 'success' });
      } else {
        // Create new faculty with pre-filled user_id
        await axios.post('/api/faculty', facultyFormData);
        setMessage({ text: 'Faculty added successfully!', type: 'success' });
        setShowFacultyForm(false);
      }
      setFacultyFormData({
        user_id: '',
        fac_name: '',
        designation: '',
        role: '',
        dept_id: '',
        join_date: '',
        email: '',
        phone: '',
      });
      setEditingFaculty(null);
      fetchFaculty();
    } catch (err) {
      console.error(err);
      setMessage({
        text: err.response?.data?.error || 'Failed to save faculty',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (fac) => {
    setEditingFaculty(fac);
    setFacultyFormData({
      user_id: fac.user_id || '',
      fac_name: fac.fac_name || '',
      designation: fac.designation || '',
      role: fac.role || '',
      dept_id: fac.dept_id || '',
      join_date: fac.join_date ? fac.join_date.split('T')[0] : '',
      email: fac.email || '',
      phone: fac.phone || '',
    });
    setShowFacultyForm(true);  // Show faculty form for edit
  };

  const handleDelete = async (facId) => {
    if (!window.confirm('Are you sure? This will delete the faculty and their user account.')) return;

    try {
      await axios.delete(`/api/faculty/${facId}`);
      setMessage({ text: 'Faculty deleted successfully!', type: 'success' });
      fetchFaculty();
    } catch (err) {
      const backendError = err.response?.data?.error || 'Failed to delete faculty';

      if (err.response?.status === 409) {
        const confirmForceDelete = window.confirm(
          'This faculty has dependent records (courses/content/plans/messages/etc).\n\n' +
          'Do you want to force delete the faculty and all related data? This cannot be undone.'
        );

        if (confirmForceDelete) {
          try {
            await axios.delete(`/api/faculty/${facId}?force=true`);
            setMessage({ text: 'Faculty and related records deleted successfully!', type: 'success' });
            fetchFaculty();
            return;
          } catch (forceErr) {
            setMessage({
              text: forceErr.response?.data?.error || 'Force delete failed',
              type: 'error'
            });
            return;
          }
        }
      }

      setMessage({
        text: backendError,
        type: 'error'
      });
    }
  };

  // ── Bulk Upload Functions ──

  const downloadTemplate = () => {
    const headers = ['user_name', 'email', 'fac_name', 'department_name', 'designation', 'role', 'join_date', 'phone'];
    const sampleRow = ['john.doe', 'john@college.edu', 'Dr. John Doe', 'Computer Science', 'Assistant Professor', 'faculty', '2024-01-15', '9876543210'];
    const csvContent = [headers.join(','), sampleRow.join(',')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'faculty_bulk_upload_template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) return;
    setBulkUploading(true);
    setBulkResults(null);
    try {
      const formData = new FormData();
      formData.append('file', bulkFile);
      const res = await axios.post('/api/faculty/bulk-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setBulkResults(res.data);
      fetchFaculty(); // Refresh the list
    } catch (err) {
      setBulkResults({
        message: err.response?.data?.error || 'Upload failed',
        success_count: 0,
        error_count: 0,
        results: { success: [], errors: [{ row: '-', error: err.response?.data?.error || 'Upload failed' }] }
      });
    } finally {
      setBulkUploading(false);
    }
  };

  const closeBulkUpload = () => {
    setShowBulkUpload(false);
    setBulkFile(null);
    setBulkResults(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const filteredFaculty = facultyList.filter((fac) => {
    const term = searchTerm.toLowerCase();
    return (
      (fac.fac_name || '').toLowerCase().includes(term) ||
      (fac.designation || '').toLowerCase().includes(term) ||
      (fac.role || '').toLowerCase().includes(term) ||
      (fac.dept_name || '').toLowerCase().includes(term) ||
      (fac.email || '').toLowerCase().includes(term) ||
      (fac.phone && fac.phone.includes(searchTerm))
    );
  });

  return (
    <div style={styles.container}>
      <div style={styles.contentWrapper}>
        {/* Header */}
        <header style={styles.header}>
          <div style={styles.headerLeft}>
            <div style={styles.iconBg}>
              <UserCircle size={28} style={styles.headerIcon} />
            </div>
            <div>
              <h1 style={styles.title}>Faculty Management</h1>
              <p style={styles.subtitle}>Add and manage faculty members</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <button
              style={{ ...styles.newBtn, background: 'linear-gradient(135deg, #7c3aed, #6d28d9)' }}
              onClick={() => setShowBulkUpload(true)}
            >
              <Upload size={20} />
              Bulk Upload
            </button>
            <button
              style={styles.newBtn}
              onClick={() => {
                setEditingFaculty(null);
                setShowFacultyForm(false);
                setUserFormData({ user_name: '', user_type: 'faculty', password: '', status: 1 });
                setFacultyFormData({ user_id: '', fac_name: '', designation: '', role: '', dept_id: '', join_date: '', email: '', phone: '' });
              }}
            >
              <Plus size={20} />
              {editingFaculty ? 'Cancel Edit' : 'New Faculty'}
            </button>
          </div>
        </header>

        {/* Bulk Upload Modal */}
        {showBulkUpload && (
          <div style={bulkStyles.overlay}>
            <div style={bulkStyles.modal}>
              <div style={bulkStyles.modalHeader}>
                <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Upload size={24} />
                  Bulk Upload Faculty
                </h2>
                <button onClick={closeBulkUpload} style={bulkStyles.closeBtn}>
                  <X size={22} />
                </button>
              </div>

              <div style={bulkStyles.modalBody}>
                {/* Step 1: Download Template */}
                <div style={bulkStyles.stepCard}>
                  <div style={bulkStyles.stepNumber}>1</div>
                  <div style={{ flex: 1 }}>
                    <h3 style={bulkStyles.stepTitle}>Download CSV Template</h3>
                    <p style={bulkStyles.stepDesc}>
                      Download the template, fill in faculty details, and save as CSV.
                    </p>
                    <div style={bulkStyles.fieldInfo}>
                      <p style={{ margin: '0 0 6px 0', fontWeight: '600', fontSize: '13px' }}>Required fields:</p>
                      <div style={bulkStyles.fieldTags}>
                        {['user_name', 'email', 'fac_name', 'department_name'].map(f => (
                          <span key={f} style={bulkStyles.reqTag}>{f}</span>
                        ))}
                      </div>
                      <p style={{ margin: '8px 0 6px 0', fontWeight: '600', fontSize: '13px' }}>Optional fields:</p>
                      <div style={bulkStyles.fieldTags}>
                        {['designation', 'role', 'join_date (YYYY-MM-DD)', 'phone'].map(f => (
                          <span key={f} style={bulkStyles.optTag}>{f}</span>
                        ))}
                      </div>
                    </div>
                    <button onClick={downloadTemplate} style={bulkStyles.downloadBtn}>
                      <Download size={18} />
                      Download Template
                    </button>
                  </div>
                </div>

                {/* Step 2: Upload File */}
                <div style={bulkStyles.stepCard}>
                  <div style={bulkStyles.stepNumber}>2</div>
                  <div style={{ flex: 1 }}>
                    <h3 style={bulkStyles.stepTitle}>Upload Filled CSV</h3>
                    <p style={bulkStyles.stepDesc}>
                      Select your completed CSV file to bulk-create faculty accounts.
                    </p>
                    <div style={bulkStyles.uploadArea}
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f?.name.endsWith('.csv')) setBulkFile(f); }}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv"
                        style={{ display: 'none' }}
                        onChange={(e) => setBulkFile(e.target.files[0] || null)}
                      />
                      {bulkFile ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <FileText size={24} style={{ color: '#7c3aed' }} />
                          <span style={{ fontWeight: '600', color: '#1e293b' }}>{bulkFile.name}</span>
                          <button onClick={(e) => { e.stopPropagation(); setBulkFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}>
                            <X size={18} />
                          </button>
                        </div>
                      ) : (
                        <div style={{ textAlign: 'center', color: '#6b7280' }}>
                          <Upload size={32} style={{ marginBottom: '8px', color: '#9ca3af' }} />
                          <p style={{ margin: 0, fontSize: '14px' }}>Click to select or drag & drop CSV file</p>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={handleBulkUpload}
                      disabled={!bulkFile || bulkUploading}
                      style={{
                        ...bulkStyles.uploadBtn,
                        ...(!bulkFile || bulkUploading ? { opacity: 0.5, cursor: 'not-allowed' } : {})
                      }}
                    >
                      {bulkUploading ? (
                        <>
                          <div style={styles.spinner}></div>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload size={18} />
                          Upload & Create Faculty
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Results */}
                {bulkResults && (
                  <div style={bulkStyles.resultsCard}>
                    <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>
                      Upload Results
                    </h3>
                    <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                      <div style={bulkStyles.statBox('#dcfce7', '#166534')}>
                        <CheckCircle size={20} />
                        <span>{bulkResults.success_count || 0} Succeeded</span>
                      </div>
                      <div style={bulkStyles.statBox('#fee2e2', '#dc2626')}>
                        <AlertCircle size={20} />
                        <span>{bulkResults.error_count || 0} Failed</span>
                      </div>
                    </div>

                    {/* Success list */}
                    {bulkResults.results?.success?.length > 0 && (
                      <div style={{ marginBottom: '12px' }}>
                        <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#166534' }}>Successfully Created:</h4>
                        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          {bulkResults.results.success.map((s, i) => (
                            <div key={i} style={bulkStyles.resultRow('#f0fdf4')}>
                              <CheckCircle size={16} style={{ color: '#16a34a', flexShrink: 0 }} />
                              <span style={{ flex: 1 }}>
                                <strong>{s.fac_name}</strong> — {s.fac_id}
                                {s.email_sent ? ' (credentials emailed)' : ` (email failed, password: ${s.password})`}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Error list */}
                    {bulkResults.results?.errors?.length > 0 && (
                      <div>
                        <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#dc2626' }}>Errors:</h4>
                        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          {bulkResults.results.errors.map((e, i) => (
                            <div key={i} style={bulkStyles.resultRow('#fef2f2')}>
                              <AlertCircle size={16} style={{ color: '#dc2626', flexShrink: 0 }} />
                              <span>Row {e.row}: {e.error}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Add User Form (Step 1) */}
        {!editingFaculty && (
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h2 style={styles.cardTitle}>
                <Key size={24} />
                Step 1: Create User Account
              </h2>
            </div>

            {message.text && !showFacultyForm && (
              <div style={{
                ...styles.message,
                ...(message.type === 'success' ? styles.successMsg : styles.errorMsg)
              }}>
                {message.text}
              </div>
            )}

            <form onSubmit={handleCreateUser} style={styles.form}>
              <div style={styles.formGrid}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>
                    Username <span style={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    name="user_name"
                    value={userFormData.user_name}
                    onChange={handleUserInputChange}
                    placeholder="e.g. john.doe"
                    style={styles.input}
                    required
                  />
                  {formErrors.user_name && <p style={styles.errorText}>{formErrors.user_name}</p>}
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>User Type</label>
                  <input
                    type="text"
                    value={userFormData.user_type}
                    disabled
                    style={{ ...styles.input, background: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>
                    Email <span style={styles.required}>*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={userFormData.email}
                    onChange={handleUserInputChange}
                    placeholder="faculty@college.edu"
                    style={styles.input}
                    required
                  />
                  <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                    Credentials will be sent to this email
                  </p>
                  {formErrors.email && <p style={styles.errorText}>{formErrors.email}</p>}
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Status</label>
                  <select
                    name="status"
                    value={userFormData.status}
                    onChange={handleUserInputChange}
                    style={styles.select}
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
              </div>

              <div style={styles.formActions}>
                <button type="submit" disabled={loading} style={{
                  ...styles.submitBtn,
                  ...(loading && styles.submitBtnDisabled)
                }}>
                  {loading ? (
                    <>
                      <div style={styles.spinner}></div>
                      Creating User...
                    </>
                  ) : (
                    'Create User Account'
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Faculty Details Form (Step 2 or Edit) */}
        {(showFacultyForm || editingFaculty) && (
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h2 style={styles.cardTitle}>
                <Layers size={24} />
                {editingFaculty ? 'Edit Faculty Details' : 'Step 2: Add Faculty Details'}
              </h2>
            </div>

            {message.text && showFacultyForm && (
              <div style={{
                ...styles.message,
                ...(message.type === 'success' ? styles.successMsg : styles.errorMsg)
              }}>
                {message.text}
              </div>
            )}

            <form onSubmit={handleSubmitFaculty} style={styles.form}>
              <div style={styles.formGrid}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>User ID (Auto-filled)</label>
                  <input
                    type="text"
                    value={facultyFormData.user_id}
                    disabled
                    style={{ ...styles.input, background: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>
                    Faculty Name <span style={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    name="fac_name"
                    value={facultyFormData.fac_name}
                    onChange={handleFacultyInputChange}
                    placeholder="e.g. Jane Smith"
                    style={styles.input}
                    required
                  />
                  {formErrors.fac_name && <p style={styles.errorText}>{formErrors.fac_name}</p>}
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>
                    Department <span style={styles.required}>*</span>
                  </label>
                  <select
                    name="dept_id"
                    value={facultyFormData.dept_id}
                    onChange={handleFacultyInputChange}
                    style={styles.select}
                    required
                  >
                    <option value="">Select Department</option>
                    {departments.map((dept) => (
                      <option key={dept.dept_id} value={dept.dept_id}>
                        {dept.dept_name} ({dept.dept_code})
                      </option>
                    ))}
                  </select>
                  {formErrors.dept_id && <p style={styles.errorText}>{formErrors.dept_id}</p>}
                </div>

                <div style={{ gridColumn: '1 / -1', padding: '12px', background: '#f0f9ff', borderRadius: '6px', border: '1px solid #bae6fd' }}>
                  <p style={{ margin: 0, fontSize: '13px', color: '#0369a1' }}>
                    ℹ️ <strong>Note:</strong> Faculty will complete their profile (designation, role, join date, email, phone) after their first login via the Edit Profile option.
                  </p>
                </div>
              </div>

              <div style={styles.formActions}>
                <button
                  type="button"
                  onClick={() => {
                    setShowFacultyForm(false);
                    setEditingFaculty(null);
                  }}
                  style={styles.cancelBtn}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    ...styles.submitBtn,
                    ...(loading && styles.submitBtnDisabled),
                  }}
                >
                  {loading ? (
                    <>
                      <div style={styles.spinner} />
                      Saving...
                    </>
                  ) : editingFaculty ? (
                    'Update Faculty'
                  ) : (
                    'Add Faculty'
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Faculty List */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h2 style={styles.cardTitle}>
              <BookOpen size={24} />
              Faculty List ({filteredFaculty.length})
            </h2>
            <div style={styles.searchContainer}>
              <Search size={20} style={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search faculty..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={styles.searchInput}
              />
            </div>
          </div>

          <div style={styles.tableContainer}>
            {filteredFaculty.length === 0 ? (
              <div style={styles.emptyState}>
                <UserCircle size={64} style={{ color: '#9ca3af' }} />
                <h3 style={styles.emptyTitle}>No faculty found</h3>
                <p style={styles.emptySubtitle}>Add new faculty to get started</p>
              </div>
            ) : (
              <div style={styles.tableWrapper}>
                <table style={styles.table}>
                  <thead>
                    <tr style={styles.tableHeadRow}>
                      <th style={styles.tableHead}>Faculty Name</th>
                      <th style={styles.tableHead}>Designation</th>
                      <th style={styles.tableHead}>Role</th>
                      <th style={styles.tableHead}>Department</th>
                      <th style={styles.tableHead}>Email</th>
                      <th style={styles.tableHead}>Phone</th>
                      <th style={styles.tableHead}>Join Date</th>
                      <th style={styles.tableHead}>Faculty ID</th>
                      <th style={styles.tableHead}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFaculty.map((fac) => (
                      <tr key={fac.fac_id} style={styles.tableRow}>
                        <td style={styles.tableCell}>
                          <div style={styles.facName}>{fac.fac_name}</div>
                        </td>
                        <td style={styles.tableCell}>
                          {fac.designation || '-'}
                        </td>
                        <td style={styles.tableCell}>
                          {fac.role || '-'}
                        </td>
                        <td style={styles.tableCell}>
                          {fac.dept_name || '-'}
                        </td>
                        <td style={styles.tableCell}>
                          {fac.email || '-'}
                        </td>
                        <td style={styles.tableCell}>
                          {fac.phone || '-'}
                        </td>
                        <td style={styles.tableCell}>
                          {fac.join_date || '-'}
                        </td>
                        <td style={styles.tableCell}>
                          <code style={styles.facId}>{fac.fac_id}</code>
                        </td>
                        <td style={styles.tableCell}>
                          <div style={styles.actionButtons}>
                            <button
                              onClick={() => handleEdit(fac)}
                              style={styles.editBtn}
                              title="Edit"
                            >
                              <Edit3 size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(fac.fac_id)}
                              style={styles.deleteBtn}
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Styles (consistent with AcademicYearManagement)
const styles = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '32px',
    background: '#f8fafc',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  contentWrapper: {
    maxWidth: '1400px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  iconBg: {
    width: '56px',
    height: '56px',
    background: 'linear-gradient(135deg, #1e3a8a, #0f766e)',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerIcon: {
    color: 'white',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0,
  },
  subtitle: {
    color: '#64748b',
    fontSize: '16px',
    margin: 0,
  },
  newBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #1e3a8a, #0f766e)',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '15px',
  },
  card: {
    background: '#fff',
    borderRadius: '20px',
    boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
    border: '1px solid #e2e8f0',
    overflow: 'hidden',
    marginBottom: '32px',
  },
  cardHeader: {
    padding: '28px 32px',
    borderBottom: '1px solid #f1f5f9',
  },
  cardTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  message: {
    padding: '16px 24px',
    borderRadius: '12px',
    margin: '0 32px 24px 32px',
    fontWeight: '500',
  },
  successMsg: {
    background: 'linear-gradient(90deg, #dcfce7, #bbf7d0)',
    color: '#166534',
    border: '1px solid #86efac',
  },
  errorMsg: {
    background: 'linear-gradient(90deg, #fee2e2, #fecaca)',
    color: '#dc2626',
    border: '1px solid #fca5a5',
  },
  form: {
    padding: '32px',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '24px',
    marginBottom: '32px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
  },
  required: {
    color: '#ef4444',
  },
  input: {
    padding: '14px 16px',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    fontSize: '15px',
    outline: 'none',
  },
  select: {
    padding: '14px 16px',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    fontSize: '15px',
    outline: 'none',
  },
  errorText: {
    color: '#ef4444',
    fontSize: '12px',
    marginTop: '4px',
  },
  formActions: {
    display: 'flex',
    gap: '16px',
    justifyContent: 'flex-end',
  },
  cancelBtn: {
    padding: '10px 20px',
    background: '#f8fafc',
    color: '#64748b',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    cursor: 'pointer',
    fontWeight: '500',
  },
  submitBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '14px 28px',
    background: 'linear-gradient(135deg, #1e3a8a, #0f766e)',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    fontSize: '15px',
  },
  submitBtnDisabled: {
    opacity: 0.7,
    cursor: 'not-allowed',
  },
  searchContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    maxWidth: '300px',
  },
  searchIcon: {
    position: 'absolute',
    left: '16px',
    color: '#9ca3af',
    pointerEvents: 'none',
  },
  searchInput: {
    width: '100%',
    padding: '12px 16px 12px 48px',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    fontSize: '15px',
    outline: 'none',
  },
  tableContainer: {
    padding: '0',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeadRow: {
    background: '#f8fafc',
  },
  tableHead: {
    padding: '20px 24px',
    textAlign: 'left',
    fontSize: '14px',
    fontWeight: '700',
    color: '#374151',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  tableRow: {
    borderBottom: '1px solid #f1f5f9',
    transition: 'all 0.2s ease',
  },
  tableCell: {
    padding: '20px 24px',
  },
  facName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b',
  },
  facId: {
    background: '#f3f4f6',
    color: '#6b7280',
    padding: '6px 12px',
    borderRadius: '8px',
    fontSize: '13px',
    fontFamily: 'monospace',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
  },
  editBtn: {
    padding: '10px',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
    border: 'none',
    cursor: 'pointer',
    color: '#1e40af',
    transition: 'all 0.2s ease',
  },
  deleteBtn: {
    padding: '10px',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
    border: 'none',
    cursor: 'pointer',
    color: '#dc2626',
    transition: 'all 0.2s ease',
  },
  emptyState: {
    textAlign: 'center',
    padding: '80px 40px',
    color: '#6b7280',
  },
  emptyTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#374151',
    margin: '16px 0 8px 0',
  },
  emptySubtitle: {
    fontSize: '16px',
    margin: 0,
  },
  spinner: {
    width: 18,
    height: 18,
    border: '2px solid transparent',
    borderTop: '2px solid white',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
};

// Bulk upload modal styles
const bulkStyles = {
  overlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    backdropFilter: 'blur(4px)',
  },
  modal: {
    background: '#fff',
    borderRadius: '20px',
    width: '680px',
    maxHeight: '90vh',
    overflow: 'hidden',
    boxShadow: '0 25px 60px rgba(0,0,0,0.3)',
    display: 'flex',
    flexDirection: 'column',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px 28px',
    borderBottom: '1px solid #e5e7eb',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#6b7280',
    padding: '6px',
    borderRadius: '8px',
  },
  modalBody: {
    padding: '28px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  stepCard: {
    display: 'flex',
    gap: '16px',
    padding: '20px',
    background: '#f8fafc',
    borderRadius: '14px',
    border: '1px solid #e2e8f0',
  },
  stepNumber: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #7c3aed, #6d28d9)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '700',
    fontSize: '16px',
    flexShrink: 0,
  },
  stepTitle: {
    margin: '0 0 4px 0',
    fontSize: '16px',
    fontWeight: '700',
    color: '#1e293b',
  },
  stepDesc: {
    margin: '0 0 12px 0',
    fontSize: '13px',
    color: '#64748b',
  },
  fieldInfo: {
    marginBottom: '14px',
    padding: '12px',
    background: '#fff',
    borderRadius: '10px',
    border: '1px solid #e5e7eb',
  },
  fieldTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  reqTag: {
    padding: '3px 10px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600',
    background: '#fef3c7',
    color: '#92400e',
    border: '1px solid #fde68a',
  },
  optTag: {
    padding: '3px 10px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '500',
    background: '#e0e7ff',
    color: '#3730a3',
    border: '1px solid #c7d2fe',
  },
  downloadBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    background: 'linear-gradient(135deg, #1e3a8a, #0f766e)',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '14px',
  },
  uploadArea: {
    border: '2px dashed #d1d5db',
    borderRadius: '12px',
    padding: '28px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '14px',
    transition: 'border-color 0.2s',
    minHeight: '80px',
  },
  uploadBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #7c3aed, #6d28d9)',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '14px',
  },
  resultsCard: {
    padding: '20px',
    background: '#fff',
    borderRadius: '14px',
    border: '1px solid #e2e8f0',
  },
  statBox: (bg, color) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 18px',
    borderRadius: '10px',
    fontWeight: '600',
    fontSize: '14px',
    background: bg,
    color: color,
  }),
  resultRow: (bg) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 12px',
    borderRadius: '8px',
    background: bg,
    marginBottom: '4px',
    fontSize: '13px',
  }),
};

// Global animation
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin { to { transform: rotate(360deg); } }
  tr:hover { background: #f8fafc !important; }
`;
document.head.appendChild(styleSheet);

export default FacultyManagement;
